import {
  BooleanInput,
  NumberInput,
  coerceBooleanProperty,
  coerceNumberProperty,
} from '@angular/cdk/coercion';
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import {
  AbstractControl,
  UntypedFormControl,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { Observable, Subscription } from 'rxjs';
import { debounceTime, tap } from 'rxjs/operators';
import { v4 as uuidv4 } from 'uuid';
import { SelectOption, SelectOptionBadgePerson } from './types';
import { MatSelect } from '@angular/material/select';

@Component({
  selector: 'ciao-form-field',
  templateUrl: './field.component.html',
  styleUrls: ['./field.component.less', './field.component.theme.scss'],
  standalone: false,
})
export class FieldComponent<T = any>
  implements OnInit, AfterViewInit, OnDestroy
{
  private static idsInUse = {} as { [id: string]: FieldComponent };
  @Input() type:
    | 'input'
    | 'text'
    | 'phoneNumber'
    | 'satPhoneNumber'
    | 'number'
    | 'latlong'
    | 'textarea'
    | 'dropdown'
    | 'combobox'
    | 'multiselect'
    | 'checkbox'
    | 'radio'
    | 'toggle'
    | 'date'
    | 'dateRange'
    | 'datetime' = 'input';
  @Input() subtype: string;
  @Input() label?: string;
  @Input() subtext: string;
  @Input() placeholder: string;
  @Input() toolTip: string;
  @Input() toolTipTitle?: string;
  @Input() required: BooleanInput;
  @Input() radioValue: string;

  /** Use requiredProxy to add the red required asterix to the label without having an error when this field is blank.  Useful when this input acts as a validation proxy for another FormControl */
  @Input() requiredProxy: BooleanInput;
  get _required() {
    return coerceBooleanProperty(this.required);
  }
  @Input() validators: ValidatorFn[];
  @Input() name: string;
  @Input() width: string;
  @Input() defaultValue: any = '';
  /** Angular's select compareWith attribute.  Only applies on multiselect or normal dropdown (will work with autocomplete dropdowns at a later date)  (https://angular.io/api/forms/SelectControlValueAccessor#customizing-option-selection) */
  @Input() compareWith: (c1: T, c2: T) => boolean = (c1, c2) => {
    if (c1 instanceof Object) {
      return c1 === c2;
    } else {
      return c1?.toString() === c2?.toString();
    }
  };
  /** Material's multiselect customTrigger attribute.  Only applies when {@link type}='multiselect' | 'dropdown' */
  @Input() customTrigger: (values: T[]) => string;
  @Input() formControlInput: AbstractControl;
  get formControlInputAsFormControl() {
    return this.formControlInput as UntypedFormControl;
  }
  /** HTML input min attribute.  Only applies when {@link type}='number' */
  @Input() min?: NumberInput;
  get _min() {
    return coerceNumberProperty(this.min);
  }
  /** HTML input max attribute.  Only applies when {@link type}='number' */
  @Input() max?: NumberInput;
  get _max() {
    return coerceNumberProperty(this.max);
  }
  /** HTML input step attribute.  Only applies when {@link type}='number' */
  @Input() step?: NumberInput;
  get _step() {
    return coerceNumberProperty(this.step);
  }
  // secretFormControl?: AbstractControl = new FormControl('');
  // get secretFormControlAsFormControl() {
  //   return this.secretFormControl as FormControl;
  // }
  @Input() selectOptions$: Observable<SelectOption<T>[]>;
  /** For combobox only: when true, form control stores full option { value, label } so the dropdown keeps showing the selected label after selection and refresh. Default false (control stores only option.value). */
  @Input() storeFullOption?: BooleanInput = false;
  get _storeFullOption() {
    return coerceBooleanProperty(this.storeFullOption);
  }
  @Input() autocomplete?: BooleanInput = false;
  get _autocomplete() {
    return coerceBooleanProperty(this.autocomplete);
  }
  @Input() inputId: string;
  @Input() helpText: string;

  @Input() agGridContext?: boolean = false;

  /**
   * For `checkbox` fields, when set, renders the guest badge next to the
   * checkbox label. For select-style fields (dropdown/combobox/multiselect) the
   * badge is driven per-option from {@link SelectOption.badgePerson} instead.
   */
  @Input() badgePerson?: SelectOptionBadgePerson | null;

  @Output() selectionChangeEvent: Observable<any>;

  @Output() blurEvent: EventEmitter<any> = new EventEmitter<any>();

  @ViewChild('focusTarget') focusTarget:
    | ElementRef<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
    | MatSelect;

  subscriptions = new Subscription();
  constructor(private readonly el: ElementRef<HTMLDivElement>) {}

  /**
   * Split helpers used when rendering a people-typed select option so the
   * guest badge can sit between the name and the email portion of a
   * `"First Last (email)"` display label. Returns the whole label unchanged
   * when there's no email parenthetical.
   */
  guestNamePart(label: string | null | undefined): string {
    if (!label) {
      return '';
    }
    const idx = label.lastIndexOf(' (');
    return idx > 0 ? label.substring(0, idx) : label;
  }
  guestEmailPart(label: string | null | undefined): string {
    if (!label) {
      return '';
    }
    const idx = label.lastIndexOf(' (');
    return idx > 0 ? label.substring(idx) : '';
  }

  /** Make this form field receive focus */
  Focus() {
    // Must be asynchronous because this occurs inside something that doesn't let the focus happen immediately.
    setTimeout(() => {
      if (this.focusTarget instanceof ElementRef) {
        this.focusTarget.nativeElement.focus();
      } else {
        this.focusTarget.focus();
        this.focusTarget.open();
      }
    });
  }

  onFieldBlur(event) {
    this.blurEvent.emit(event);
  }

  ngOnInit(): void {
    this.calculateId();
    if (!this.formControlInput) {
      this.formControlInput = new UntypedFormControl(this.defaultValue, {
        validators: this.validators,
      });
    }
    this.selectionChangeEvent = this.formControlInput.valueChanges;
    if (this.type === 'phoneNumber') {
      let sub1 = this.formControlInput.valueChanges
        .pipe(
          debounceTime(400),
          tap((value: string) => {
            let newVal = value?.replace(/\D/g, '') || '';
            let newStringVal = '';
            const zip = newVal.substring(0, 3);
            const middle = newVal.substring(3, 6);
            const last = newVal.substring(6, 10);

            if (newVal.length > 6) {
              newStringVal = `(${zip}) ${middle} - ${last}`;
            } else if (newVal.length > 3) {
              newStringVal = `(${zip}) ${middle}`;
            } else if (newVal.length > 0) {
              newStringVal = `(${zip})`;
            }
            if (newStringVal != value) {
              this.formControlInput.patchValue(newStringVal);
            }
          })
        )
        .subscribe();
      this.subscriptions.add(sub1);
    } else if (this.type === 'satPhoneNumber') {
      let sub2 = this.formControlInput.valueChanges
        .pipe(
          debounceTime(400),
          tap((value: string) => {
            let newVal = value?.replace(/\D/g, '') || '';
            let newStringVal = '';
            const chunks = [
              newVal.substring(0, 3),
              newVal.substring(3, 5),
              newVal.substring(5, 8),
              newVal.substring(8, 11),
              newVal.substring(11, 15),
            ];

            newStringVal = chunks.filter((x) => !!x).join('-');
            if (newStringVal != value) {
              this.formControlInput.patchValue(newStringVal);
            }
          })
        )
        .subscribe();
      this.subscriptions.add(sub2);
    } else if (this.type === 'datetime') {
      // No longer need SecretFormControl
    }
    // block scope
    {
      let testControl = new UntypedFormControl('');
      let validator = this.formControlInput.validator;
      if (validator) {
        let testErrors = validator(testControl);
        if (testErrors?.required) {
          this.required = true;
        }
      }
    }
    let fn = this.formControlInput.validator;
    let validators = fn ? [fn] : [];
    if (this.required) {
      validators.push(Validators.required);
    }
    if (this.type === 'phoneNumber') {
      validators.push(CellPhoneValidator);
    }
    if (this.type === 'satPhoneNumber') {
      validators.push(SatPhoneValidator);
    }
    this.formControlInput.setValidators(validators);
  }
  ngAfterViewInit(): void {
    this.el.nativeElement
      .querySelectorAll<HTMLInputElement>('input[aria-multiline]')
      .forEach((input) => {
        input.attributes.removeNamedItem('aria-multiline');
      });
  }
  ngOnDestroy(): void {
    Object.entries(FieldComponent.idsInUse).forEach(([id, value]) => {
      if (value === this) {
        delete FieldComponent.idsInUse[id];
      }
    });
    this.subscriptions.unsubscribe();
  }

  calculateId() {
    if (!this.inputId && !this.label) {
      throw new Error(
        'inputId or Label must be defined before calculating the id of the form field'
      );
    }
    let inputId = this.inputId;
    if (!inputId)
      inputId = 'form_field-' + this.label?.toLowerCase().replace(/\W+/g, '_');
    if (FieldComponent.idsInUse[inputId]) {
      inputId += '_' + uuidv4();
    }
    FieldComponent.idsInUse[inputId] = this;
    this.inputId = inputId;
  }
}

export const CellPhoneValidator: ValidatorFn = (control) => {
  if (!control.value) {
    return null;
  }
  if (control.value.replace(/\D/g, '').length === 10) {
    return null;
  } else {
    return {
      cellPhonePattern: 'Phone does not match 10-digit cell phone pattern',
    };
  }
};
export const SatPhoneValidator: ValidatorFn = (control) => {
  if (!control.value) {
    return null;
  }
  if (control.value.replace(/\D/g, '').length === 15) {
    return null;
  } else {
    return {
      satPhonePattern:
        'Satellite Phone does not match 15-digit sat phone pattern',
    };
  }
};
